import { race, take } from 'typed-redux-saga';
import { Action } from 'redux';

interface SucceededResult<SA extends Action> {
  readonly type: 'succeeded';
  readonly action: SA;
}

interface FailedResult<FA extends Action> {
  readonly type: 'failed';
  readonly action: FA;
}

type WaitResult<SA extends Action, FA extends Action> = SucceededResult<SA> | FailedResult<FA>;

type WaitResultAction<T extends string> = Action<T> & {
  readonly type: T;
  readonly payload: {
    readonly requestedActionId: number;
  };
};

function* waitForSucceededOrFailedAction<
  SAT extends string,
  FAT extends string,
  SA extends WaitResultAction<SAT>,
  FA extends WaitResultAction<FAT>
>(actionId: number, succeededType: SAT, failedType: FAT): Generator<unknown, WaitResult<SA, FA>> {
  let isSameActionId: boolean = false;
  let result: WaitResult<SA, FA> | undefined;
  do {
    const { failed, succeeded }: { failed: FA | undefined; succeeded: SA | undefined } =
      yield* race({
        succeeded: take<SA>(succeededType),
        failed: take<FA>(failedType)
      });

    isSameActionId =
      succeeded?.payload.requestedActionId === actionId ||
      failed?.payload.requestedActionId === actionId;

    if (isSameActionId) {
      if (succeeded !== undefined) {
        result = {
          type: 'succeeded',
          action: succeeded
        };
      } else if (failed !== undefined) {
        result = {
          type: 'failed',
          action: failed
        };
      } else {
        throw new Error('Case not possible, but needed for the compiler. Please debug');
      }
    }
  } while (result === undefined);
  return result;
}

export default waitForSucceededOrFailedAction;
